import PropTypes from 'prop-types';
import React from 'react';
import StripTags from 'striptags';

import { Button } from '$partials/ui';
import { StaticTitle } from '$partials/layout/side-titles.jsx';
import { Image } from '$partials/media';
import { useLanguage } from '$contexts/language.jsx';

/* =========================================================================  */
/* Component(s) */
/* =========================================================================  */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const Author = ({
    biography       = null,
    displayLocation = null,
    emailAddress    = null,
    image           = null,
    introduction    = null,
    relatedPerson   = null,
    role            = null,
    sideTitle       = null,
    title           = null
}) => {
    // hooks
    const { translate: t } = useLanguage();

    // misc
    const _biography = relatedPerson?.biography ? relatedPerson.biography : biography;
    const _displayLocation = relatedPerson?.displayLocation ? relatedPerson.displayLocation : displayLocation;
    const _emailAddress = relatedPerson?.emailAddress ? relatedPerson.emailAddress : emailAddress;
    const _image = relatedPerson?.image ? relatedPerson.image : image;
    const _introduction = relatedPerson?.introduction ? relatedPerson.introduction : introduction;
    const _role = relatedPerson?.role ? relatedPerson.role : role;
    const _title = relatedPerson?.title ? relatedPerson.title : title;

    // render
    return (
        <section className='module-author'>
            <div className='pr-gutter mxn-gutter'> {/* outer grid gutter */}
                <div className='flex flex-wrap'>
                    {/* side title */}
                    <div className='absolute top-0 md-relative md-col-2'>
                        {sideTitle && (
                            <div className='px-gutter'>
                                <p className='type-style-5 side-title side-title--lesser-top'>
                                    <StaticTitle title={sideTitle} />
                                </p>
                            </div>
                        )}
                    </div>

                    {/* content */}
                    <div className='col-11 sm-col-8 md-col-7'>
                        <div className='ml9 sm-ml9 md-ml0'>
                            <div className='px-gutter'>
                                {/* introduction */}
                                {_introduction && (
                                    <h3>
                                        <div
                                            className='rich-text type-style-3 mb7'
                                            dangerouslySetInnerHTML={{ __html: StripTags(_introduction, ['mark', 'a', 'h2', 'h3', 'h4', 'hr', 'br', 'b', 'i']) }} />
                                    </h3>
                                )}

                                {/* biography */}
                                {_biography && (
                                    <div
                                        className={`rich-text mb5 sm-mb5 md-mb7${_introduction ? ' type-style-6' : ' type-style-3'}`}
                                        dangerouslySetInnerHTML={{ __html: _biography }}
                                        style={{ whiteSpace: 'break-spaces'  }}/>
                                )}

                                {/* email */}
                                {_emailAddress && (
                                    <Button
                                        className='button mb5 sm-mb0 md-mb0 capitalise'
                                        href={`mailto:${_emailAddress}`}
                                        rel='noopener noreferrer'
                                        target='_blank'>
                                        {t('Contact')}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className='col-12 sm-col-5 md-col-3'>
                        <div className='ml9 sm-ml9 md-ml0'>
                            <div className='px-gutter'>
                                {/* image */}
                                {_image && (
                                    <figure className='border-rad-10 font-0 mt5 sm-mt0 md-mt0'>
                                        <Image {..._image} />
                                    </figure>
                                )}

                                <dl>
                                    {/* name */}
                                    <dt className='accessible-hide'>
                                        {t('Name')}
                                    </dt>

                                    <dd className='type-style-3 pb3 pt4 border-bottom border-black'>
                                        {_title}
                                    </dd>

                                    {/* role */}
                                    <dt className='accessible-hide'>
                                        {t('Role')}
                                    </dt>

                                    {_role && (
                                        <dd className='type-style-6 pb3 pt4 border-bottom border-black hans-purple'>
                                            {_role}
                                        </dd>
                                    )}

                                    {/* location */}
                                    <dt className='accessible-hide'>
                                        {t('Location')}
                                    </dt>

                                    {_displayLocation && (
                                        <dd className='type-style-6 pb3 pt4 border-bottom border-black hans-purple'>
                                            {_displayLocation}
                                        </dd>
                                    )}
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

/* =========================================================================  */
/* Typing and exports */
/* =========================================================================  */

const propTypes = {
    biography      : PropTypes.string,
    displayLocation: PropTypes.string,
    emailAddress   : PropTypes.string,
    image          : PropTypes.object,
    introduction   : PropTypes.string,
    role           : PropTypes.string,
    relatedPerson  : PropTypes.object,
    sideTitle      : PropTypes.string,
    title          : PropTypes.string
};

Author.propTypes = propTypes;

export { Author };

export default Author;
